/**
 * Where replies sent from Messages point (#3737): the workshop's own mailbox
 * becomes the Reply-To of every reply and, unless switched off, gets a blind
 * copy of it. Saved as `settings.mailboxReply` through PATCH /tenants/me/settings.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';
import { api } from '@/lib/api';
import { queryKeys } from '@/hooks/queryKeys';
import { useNotify } from '@/hooks/useNotify';
import { useTenantSettings } from '@/hooks/useTenantSettings';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function MailboxReplySettings({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { tenantSettings } = useTenantSettings();
  const stored = tenantSettings?.mailboxReply;
  const storedAddress = stored?.address ?? '';
  const storedCopy = stored?.copyToMailbox !== false;

  const [address, setAddress] = useState(storedAddress);
  const [copyToMailbox, setCopyToMailbox] = useState(storedCopy);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAddress(storedAddress);
    setCopyToMailbox(storedCopy);
  }, [storedAddress, storedCopy]);

  const trimmed = address.trim();
  const invalid = trimmed !== '' && !EMAIL_RE.test(trimmed);
  const dirty = trimmed !== storedAddress || copyToMailbox !== storedCopy;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/tenants/me/settings', { mailboxReply: { address: trimmed, copyToMailbox } });
      await queryClient.invalidateQueries({ queryKey: queryKeys.tenant.settings() });
      notify.success(t('inboundEmail.reply.saved'));
    } catch {
      notify.error(tc('error'), t('inboundEmail.reply.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2} data-testid="mailbox-reply-settings">
      <Box>
        <Typography variant="subtitle1" fontWeight={600}>
          {t('inboundEmail.reply.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('inboundEmail.reply.description')}
        </Typography>
      </Box>

      <TextField
        label={t('inboundEmail.reply.addressLabel')}
        type="email"
        value={address}
        onChange={(event) => setAddress(event.target.value)}
        error={invalid}
        helperText={invalid ? t('inboundEmail.reply.invalidAddress') : t('inboundEmail.reply.addressHelp')}
        disabled={!canEdit || saving}
        fullWidth
      />

      <FormControlLabel
        control={
          <Switch
            checked={copyToMailbox}
            onChange={(event) => setCopyToMailbox(event.target.checked)}
            disabled={!canEdit || saving}
          />
        }
        label={t('inboundEmail.reply.copyLabel')}
      />

      {trimmed === '' && <Alert severity="warning">{t('inboundEmail.reply.noAddress')}</Alert>}

      {canEdit && (
        <Box>
          <Button variant="contained" onClick={() => { void save(); }} disabled={!dirty || invalid || saving}>
            {tc('save')}
          </Button>
        </Box>
      )}
    </Stack>
  );
}
